import { useCallback } from 'react';
import { shallowEqual, useSelector, useStore } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import {
  CartSnapshotProperties,
  PetType,
  buildCartSnapshot,
  legacyAddedItemProperties,
} from '../lib/analytics/cartSnapshot';
import { CustomerIO } from '../lib/analytics/customerIO';
import { CouponError, logInternalError } from '../lib/errors';
import { nextPricingOfCart, priceCartForPreview } from '../lib/pricing';
import * as types from '../types';
import { useActivePromotion } from './useActivePromoCode';

function reportTrackingError(err: unknown) {
  logInternalError(err instanceof Error ? err : new Error(String(err)), { tags: { source: 'trackAddedItemToCart' } });
}

/** how long to wait for the bag or in-app sheet to price the cart before pricing it here */
const PAGE_PRICING_WAIT_MS = 5000;

/**
 * The bag as the server prices it. Every add site routes to the bag or the in-app sheet, which price this same cart,
 * so the snapshot takes that response rather than sending a request of its own; only if none arrives in time is the
 * cart priced here. A code the server rejects is dropped, as the bag page drops it, so the snapshot does not
 * advertise it; any other failure leaves the snapshot with catalog figures only.
 */
async function priceForSnapshot(cart: types.StoreCart) {
  const pagePricing = await nextPricingOfCart(cart, PAGE_PRICING_WAIT_MS);
  if (pagePricing) {
    return { cart, cartPricing: pagePricing };
  }
  try {
    return { cart, cartPricing: await priceCartForPreview(cart) };
  } catch (err) {
    // already reported inside priceCartForPreview
    return { cart: err instanceof CouponError ? { ...cart, couponCode: undefined } : cart, cartPricing: undefined };
  }
}

/**
 * Sends Customer.io's `Added Item To Cart` with a snapshot of the whole bag. Call it right after the
 * add is dispatched: the snapshot reads the bag from the store, so it includes the item just added.
 * The added item is the one holding `addedSku` that the bag did not have when the caller last rendered;
 * the reducer generates its id, so this is how the listing's species reaches that item alone. No new item
 * means the add went to another cart (the series 3+ PDP also fills the in-app upgrade cart) or did not
 * happen, and nothing is sent.
 *
 * Promo and multi-tracker prices are only known to the server, so the event waits for the page's own pricing of
 * the bag (see `priceForSnapshot`). If pricing fails the event goes out with catalog figures, and if the snapshot
 * cannot be built it goes out with the legacy `product_name` / `product_color` only, so the campaign is never
 * left untriggered.
 */
export default function useTrackAddedItemToCart() {
  const store = useStore<types.AppState>();
  const { country, currency } = useLocale();
  const activePromotion = useActivePromotion();
  const renderedCartItemIds = useSelector(
    (state: types.AppState) => Object.keys(state.storeShop.cart.cartItems),
    shallowEqual,
  );

  return useCallback(
    async (addedSku: string, addedPetType?: PetType) => {
      const { storeShop, config } = store.getState();
      const addedItem = Object.values(storeShop.cart.cartItems).find(
        (item) => item.lineItem.sku === addedSku && !renderedCartItemIds.includes(item.cartItemId),
      );
      if (!addedItem) {
        return;
      }
      const { cart, cartPricing } = await priceForSnapshot(storeShop.cart);

      let snapshot: CartSnapshotProperties | undefined;
      try {
        snapshot = buildCartSnapshot({
          cart,
          products: config.products,
          cartPricing,
          country,
          currency,
          shopUrl: window.location.origin,
          activePromotion,
          now: new Date(),
          addedCartItemId: addedItem.cartItemId,
          addedPetType,
        });
      } catch (err) {
        // a snapshot bug degrades the event to the legacy fields below rather than dropping the trigger
        reportTrackingError(err);
      }
      try {
        CustomerIO.trackAddedItemToCart(snapshot ?? legacyAddedItemProperties(config.products, addedSku));
      } catch (err) {
        // analytics must never break the add; fire-and-forget callers do not handle a rejection
        reportTrackingError(err);
      }
    },
    [store, country, currency, activePromotion, renderedCartItemIds],
  );
}
